import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Eye, AlertCircle } from 'lucide-react';
import { SectionCard } from '@/components/shared/DashboardUI';
import { formatDate } from '@/lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { value: 'police_accident_report', label: 'Police / Accident Report' },
  { value: 'er_urgent_care_records', label: 'ER / Urgent Care Records' },
  { value: 'medical_records', label: 'Medical Records' },
  { value: 'medical_bills', label: 'Medical Bills' },
  { value: 'insurance_declaration_page', label: 'Insurance Declaration Page' },
  { value: 'insurance_claim_information', label: 'Insurance Claim Information' },
  { value: 'legal_complaint', label: 'Legal Complaint' },
  { value: 'demand_letter', label: 'Demand Letter' },
  { value: 'attorney_representation_letter', label: 'Attorney Representation Letter' },
  { value: 'settlement_offer_correspondence', label: 'Settlement Offer / Correspondence' },
  { value: 'photos_videos', label: 'Photos / Videos' },
  { value: 'other_supporting_documents', label: 'Other Supporting Documents' },
];

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
const MAX_FILE_SIZE = 25 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentUpload = ({ applicationId, canUpload = false, getAuthHeaders, userRole = 'client' }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await fetch(`${API}/applications/${applicationId}/documents`, {
        credentials: 'include', headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  }, [applicationId, getAuthHeaders]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!selectedCategory) {
      toast.error('Please select a document category first');
      return;
    }

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large (${formatFileSize(file.size)}). Maximum: 25MB`);
      return;
    }

    // Derive content_type from extension if browser didn't provide a valid one
    // (some browsers/OSes return empty file.type for .doc/.docx)
    const EXT_TO_MIME = {
      '.pdf': 'application/pdf',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
    const contentType = file.type && file.type !== 'application/octet-stream'
      ? file.type
      : EXT_TO_MIME[ext];

    setUploading(true);
    setUploadProgress('Getting upload URL...');

    try {
      // Step 1: Get pre-signed upload URL
      const urlResponse = await fetch(`${API}/applications/${applicationId}/documents/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          filename: file.name,
          content_type: contentType,
          file_size: file.size,
          category: selectedCategory
        })
      });

      if (!urlResponse.ok) {
        const err = await urlResponse.json();
        toast.error(err.detail || 'Failed to prepare upload');
        return;
      }

      const { document_id, upload_url } = await urlResponse.json();

      // Step 2: Upload to S3
      setUploadProgress('Uploading file...');
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file
      });

      if (!uploadResponse.ok) {
        toast.error('Failed to upload file to storage');
        return;
      }

      // Step 3: Confirm upload
      setUploadProgress('Confirming...');
      await fetch(`${API}/documents/${document_id}/confirm-upload`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      toast.success('Document uploaded successfully');
      setSelectedCategory('');
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(`${API}/documents/${doc.document_id}/download-url`, {
        credentials: 'include', headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        window.open(data.download_url, '_blank');
      } else {
        toast.error('Failed to get download link');
      }
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      const response = await fetch(`${API}/documents/${doc.document_id}`, {
        method: 'DELETE', credentials: 'include', headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Document deleted');
        fetchDocuments();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const fileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '🖼';
    if (mimeType === 'application/pdf') return '📄';
    return '📎';
  };

  return (
    <SectionCard title="Documents" className="mb-3" data-testid="documents-section">
      {/* Upload area (client/lawyer only) */}
      {canUpload && (
        <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#f8fafc', borderRadius: 10, border: '1px dashed #cbd5e1' }} data-testid="document-upload-area">
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem', display: 'block' }}>Document Category</label>
              <select className="mcl-form-input" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} data-testid="select-doc-category" style={{ fontSize: '0.85rem' }}>
                <option value="">Select category...</option>
                {CATEGORY_OPTIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: '0 0 auto' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: selectedCategory ? '#070689' : '#94a3b8', color: '#fff', borderRadius: 8, cursor: selectedCategory ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '0.85rem', opacity: uploading ? 0.6 : 1 }} data-testid="upload-file-btn">
                <Upload size={16} />
                {uploading ? uploadProgress : 'Choose File'}
                <input type="file" style={{ display: 'none' }} accept={ALLOWED_EXTENSIONS.join(',')} onChange={handleFileSelect} disabled={!selectedCategory || uploading} data-testid="file-input" />
              </label>
            </div>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0.5rem 0 0' }}>
            Accepted: PDF, JPG, PNG, DOC, DOCX (max 25MB)
          </p>
        </div>
      )}

      {/* Document list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Loading documents...</div>
      ) : documents.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem', color: '#94a3b8', gap: '0.5rem' }} data-testid="no-documents">
          <FileText size={32} style={{ opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>No documents uploaded yet</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} data-testid="documents-list">
          {documents.map((doc) => (
            <div key={doc.document_id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }} data-testid={`document-row-${doc.document_id}`}>
              <span style={{ fontSize: '1.25rem' }}>{fileIcon(doc.mime_type)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_filename}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <span>{doc.category_label}</span>
                  <span>{formatFileSize(doc.file_size)}</span>
                  <span>by {doc.uploader_role}</span>
                  <span>{formatDate(doc.created_at)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                <button className="mcl-action-btn" onClick={() => handleDownload(doc)} title="View / Download" data-testid={`download-doc-${doc.document_id}`} style={{ color: '#070689' }}>
                  <Eye size={15} />
                </button>
                {(canUpload || userRole === 'admin') && (
                  <button className="mcl-action-btn" onClick={() => handleDelete(doc)} title="Delete" data-testid={`delete-doc-${doc.document_id}`} style={{ color: '#ef4444' }}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};


// Disclaimer Modal for application submission
const DisclaimerModal = ({ applicationId, onAgree, onCancel, getAuthHeaders }) => {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Block ESC
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') e.preventDefault(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSubmit = async () => {
    if (!agreed) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/applications/${applicationId}/disclaimer`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      if (onAgree) onAgree();
    } catch (error) {
      toast.error('Failed to save acknowledgment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} data-testid="disclaimer-modal-overlay">
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }} data-testid="disclaimer-modal">
        {/* Header */}
        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <AlertCircle size={24} style={{ color: '#070689' }} />
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>Document Upload & Information Sharing Acknowledgment</h2>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem 2rem' }}>
          <div style={{ fontSize: '0.9rem', lineHeight: 1.7, color: '#334155' }}>
            <p>By submitting this application and uploading documents, I acknowledge and understand that the documents may contain personal information, personally identifiable information, medical information, health records, insurance information, legal records, and other sensitive case-related materials.</p>
            <p>I authorize <strong>My Case Loan, LLC</strong>, operating MyCaseLoan.com, to receive, store, process, and make these documents available to funding providers who may review my application for pre-settlement funding.</p>
            <p>I also understand that my attorney or law firm may submit these same or similar documents on my behalf as part of the funding review process.</p>
            <p>I acknowledge that My Case Loan, LLC is a platform that connects applicants with funding providers and does not provide legal, medical, financial, or funding advice. I release My Case Loan, LLC from liability related to the content, accuracy, completeness, or legal effect of the documents I or my attorney submit, except as required by applicable law.</p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '1.25rem 2rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '0 0 16px 16px' }}>
          <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '1rem' }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="disclaimer-checkbox" style={{ marginTop: 3, accentColor: '#070689', width: 18, height: 18 }} />
            <span style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 500 }}>I acknowledge and agree to the document upload and information sharing terms.</span>
          </label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handleSubmit} disabled={!agreed || submitting} data-testid="disclaimer-agree-btn" style={{ flex: 1, padding: '0.75rem', borderRadius: 8, border: 'none', background: agreed ? '#070689' : '#94a3b8', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: agreed ? 'pointer' : 'not-allowed' }}>
              {submitting ? 'Submitting...' : 'Agree & Submit Application'}
            </button>
            <button onClick={onCancel} className="mcl-btn mcl-btn-outline" data-testid="disclaimer-cancel-btn" style={{ padding: '0.75rem 1.5rem' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { DocumentUpload, DisclaimerModal };
export default DocumentUpload;
